// Playback state management for audio engine

import { AUDIO_CONSTANTS } from '../../src/config/audio-constants.js';

export interface PlaybackManager {
  stop: () => void;
}

export class PlaybackStateManager {
  private isPlaying = false;
  private globalStartTime: number | null = null;
  private globalTimeSignature: number | null = null;
  private channelIndex = 0;
  private channelManagers = new Set<PlaybackManager>();
  private activeChannels = new Set<number>();

  /**
   * Get current playback state
   */
  getState(): 'playing' | 'paused' {
    return this.isPlaying ? 'playing' : 'paused';
  }

  /**
   * Set playing state
   */
  setPlaying(playing: boolean): void {
    this.isPlaying = playing;
  }

  /**
   * Get global start time
   */
  getGlobalStartTime(): number | null {
    return this.globalStartTime;
  }

  /**
   * Set global timing
   * @param startTime Global start time
   * @param timeSignature Optional time signature
   */
  setGlobalTiming(startTime: number, timeSignature?: number): void {
    if (this.globalStartTime === null) {
      this.globalStartTime = startTime;
    }
    if (this.globalTimeSignature === null && timeSignature !== undefined) {
      this.globalTimeSignature = timeSignature;
    }
  }

  /**
   * Get global time signature
   */
  getGlobalTimeSignature(): number | null {
    return this.globalTimeSignature;
  }

  /**
   * Reset global timing
   */
  resetGlobalTiming(): void {
    this.globalStartTime = null;
    this.globalTimeSignature = null;
  }

  /**
   * Add a channel manager
   * @param manager Channel manager to track
   * @returns Channel index
   */
  addChannelManager(manager: PlaybackManager): number {
    this.channelManagers.add(manager);
    return this.channelIndex++;
  }

  /**
   * Remove a channel manager
   * @param manager Channel manager to remove
   */
  removeChannelManager(manager: PlaybackManager): void {
    this.channelManagers.delete(manager);
  }

  /**
   * Stop all channel managers
   */
  stopAll(): void {
    this.channelManagers.forEach(manager => {
      try {
        manager.stop();
      } catch (error) {
        console.error('Error stopping channel manager:', error);
      }
    });
    this.channelManagers.clear();
    this.setPlaying(false);
    this.resetGlobalTiming();
  }

  /**
   * Get number of active channels
   */
  getActiveChannelCount(): number {
    return this.channelManagers.size;
  }

  /**
   * Calculate current beat count based on tempo
   * @param audioContext Audio context for timing
   * @param tempo Current tempo in BPM
   * @param trackLengthInSamples Optional track length for modulo calculation
   * @returns Current beat count
   */
  calculateCurrentCount(
    audioContext: AudioContext,
    tempo: number,
    trackLengthInSamples?: number
  ): number {
    if (this.globalStartTime === null || this.globalTimeSignature === null) {
      return 0;
    }

    const elapsedTime =
      audioContext.currentTime -
      (audioContext.baseLatency ?? 0) -
      (audioContext.outputLatency ?? 0) -
      this.globalStartTime;

    const beatsPerSecond = tempo / AUDIO_CONSTANTS.SECONDS_PER_MINUTE;
    const totalBeats = elapsedTime * beatsPerSecond;

    // If track length provided, calculate modulo for loop position
    if (trackLengthInSamples) {
      const musicalDurationInSeconds = trackLengthInSamples / AUDIO_CONSTANTS.SAMPLE_RATE;
      const beatsInMusicalContent = musicalDurationInSeconds * beatsPerSecond;
      return totalBeats % beatsInMusicalContent;
    }

    // Otherwise return total beats
    return totalBeats;
  }

  /**
   * Check if synchronized playback is active
   */
  isSynchronized(): boolean {
    return this.globalStartTime !== null && this.globalTimeSignature !== null;
  }

  /**
   * Get elapsed time since start
   * @param audioContext Audio context for timing
   * @returns Elapsed time in seconds
   */
  getElapsedTime(audioContext: AudioContext): number {
    if (this.globalStartTime === null) {
      return 0;
    }

    return Math.max(0, audioContext.currentTime - this.globalStartTime);
  }

  /**
   * Get statistics
   */
  getStats() {
    return {
      isPlaying: this.isPlaying,
      activeChannels: this.channelManagers.size,
      isSynchronized: this.isSynchronized(),
      globalStartTime: this.globalStartTime,
      globalTimeSignature: this.globalTimeSignature,
      nextChannelIndex: this.channelIndex
    };
  }

  /**
   * Clear all state
   */
  clear(): void {
    this.stopAll();
    this.channelIndex = 0;
  }

  /**
   * Add an active channel
   * @param channel Channel index to add
   */
  addActiveChannel(channel: number): void {
    this.activeChannels.add(channel);
  }

  /**
   * Remove an active channel
   * @param channel Channel index to remove
   */
  removeActiveChannel(channel: number): void {
    this.activeChannels.delete(channel);
  }

  /**
   * Clear global timing information
   */
  clearGlobalTiming(): void {
    this.globalStartTime = null;
    this.globalTimeSignature = null;
    this.activeChannels.clear();
  }

  /**
   * Get the list of active channels
   * @returns Array of active channel indices
   */
  getActiveChannels(): number[] {
    return Array.from(this.activeChannels);
  }
}
